import React, {useState, useRef, useEffect} from "react";
import FroalaEditorComponent from "react-froala-wysiwyg";
import "froala-editor/js/plugins/align.min.js";
import "froala-editor/js/plugins/colors.min.js";
import "froala-editor/js/plugins/font_size.min.js";
import "froala-editor/js/plugins/image.min.js";
import "froala-editor/js/plugins/table.min.js";
import "froala-editor/js/plugins/fullscreen.min.js";
import "froala-editor/js/plugins/lists.min.js";
import "froala-editor/js/plugins/code_view.min.js";
import "froala-editor/js/plugins/link.min.js";
import "froala-editor/js/plugins/paragraph_format.min.js";
import { I18n } from "../../models/I18n";
import style from "./style/EditableText.module.scss";

export interface EditableTextProps {
    saveInterval?: number;
    placeholder?: string;
    text: string;
    handleSave: (text: string) => void;
    showSaveButton?: boolean;
    classes?: {
        textPresentation: string;
    };
    i18n?: I18n;
}

const defaultInterval = 5000;

export const EditableText = ({
    saveInterval,
    placeholder,
    text,
    handleSave,
    showSaveButton,
    classes,
    i18n = {}
}: EditableTextProps) => {
    const intervalRef = useRef<number>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [localText, setLocalText] = useState<string>(text || "");
    const lastSavedTextRef = useRef<string>(text || "");
    const localTextRef = useRef<string>(localText);

    const setSaveInterval = () => {
        intervalRef.current = window.setInterval(() => {
            saveText();
        }, saveInterval || defaultInterval);
    }

    const clearSaveInterval = () => {
        if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }

    const saveText = () => {
        if (lastSavedTextRef.current === localTextRef.current) {
            return;
        }

        lastSavedTextRef.current = localTextRef.current;
        handleSave(localTextRef.current);
    }

    const showEditMode = () => {
        setIsEditing(true);
        setSaveInterval();

        // since Froala's own focus event did not seem to work (probably async issue) focus the editor after a short delay
        setTimeout(() => {
            const editorInstance = document.querySelector(".fr-element");
            if (editorInstance) {
                editorInstance.focus();
            }
        }, 500);
    }

    const closeEditMode = () => {
        setIsEditing(false);
        clearSaveInterval();
        saveText();
    }

    // This is necessary to avoid the closure inside the interval callback holding onto an outdated version of localText.
    useEffect(() => {
        localTextRef.current = localText;
    }, [localText]);

    if (isEditing) {
        return (
            <React.Fragment>
                <FroalaEditorComponent
                    data-testid="froala-editor"
                    config={{
                        attribution: false,
                        editorClass: style.editor,
                        htmlAllowedTags: ["p", "h1", "h2", "h3", "h4", "h5", "h6", "li", "ul", "ol", "br", "strong", "em", "i", "u"],
                        pastePlain: true,
                        spellcheck: true,
                        toolbarSticky: false,
                        listAdvancedTypes: false,
                        key: process.env.FROALA_KEY,
                        toolbarButtons: [
                            "paragraphFormat",
                            "formatOL",
                            "formatUL",
                            "bold",
                            "italic",
                            "underline",
                            "|",
                            "undo",
                            "redo",
                        ],
                        paragraphFormat: {
                            N: "Normal",
                            H4: "Heading",
                            H5: "Subheading",
                        },
                        paragraphFormatSelection: true,
                        events: {
                            blur: () => closeEditMode(),
                            initialized: (e: Event, editor: any) => {
                                if (editor && editor.selection) {
                                    editor.selection.save();
                                    editor.selection.clear();
                                    editor.selection.restore();
                                }

                                if (editor && editor.events) {
                                    editor.events.focus();
                                }
                            }
                        },
                        placeholderText: placeholder || ""
                    }}
                    model={localText || text}
                    tag="textarea"
                    onModelChange={(value: string) => {
                        if (value !== localText) {
                            setLocalText(value);
                        }
                    }}
                />
                {showSaveButton && <div className={style.saveButtonSection}>
                    <p>{i18n.report_editable_text_auto_save_text}</p>
                    <button className="action_button modal" onClick={() => saveText()}>
                        {i18n.report_editable_text_save_button || "Save"}
                    </button>
                </div>}
            </React.Fragment>
        );
    }

    return (
        <div
            className={`${style.editor} ${style.noEditMode} ${classes?.textPresentation}`}
            onClick={() => showEditMode()}
            role="textbox"
            tabIndex={0}
            dangerouslySetInnerHTML={{ __html: localText }}
        />
    )
}

export default EditableText;
